import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Header from "../header/HeaderComponent";
import Footer from "../footer/FooterComponent";
import Seo from "../Seo/SeoComponent";
import {
    JLPT_OVERSEAS_DIRECTORY_URL,
    JLPT_OVERSEAS_MOBILE_DIRECTORY_URL,
    jlpt2026Sessions,
    jlptOverseasSites,
    type JlptOverseasSite,
} from "../../consts/jlptOverseasSites";
import { ISO_COUNTRIES, getCountryName } from "../../consts/isoCountries";
import { JLPT_COUNTRY_FALLBACKS } from "../../consts/jlptCountryFallbacks";
import { countryCodeHasJlptLocations } from "../../consts/jlptCountryDirectory";
import { useCountry } from "../../hooks/useCountry";
import styles from "./jlptTestSitesPage.module.scss";
import JlptNotice from "../JlptNotice/JlptNotice";
import { Link } from "react-router-dom";

// Resolves the ISO country code, if any, whose canonical display name
// exactly matches the current search text - used to decide whether the
// current search is "about" a known country closely enough to offer a
// no-locations-here fallback suggestion for it.
function findCountryCodeForQuery(query: string): string | null {
    const needle = query.trim().toLocaleLowerCase();
    if (!needle) return null;
    const match = ISO_COUNTRIES.find((country) => country.name.toLocaleLowerCase() === needle);
    return match?.code ?? null;
}

function SessionBadge({ available, label }: { available: boolean; label: string }) {
    const { t } = useTranslation("jlptTestSites");
    return <span className={available ? styles.available : styles.unavailable}>{label}: {available ? t("session.available") : t("session.notOffered")}</span>;
}

function Contact({ site }: { site: JlptOverseasSite }) {
    const { t } = useTranslation("jlptTestSites");
    if (!site.telephone && !site.email && !site.fax) return null;
    return <dl className={styles.contact}>
        {site.telephone && <div><dt>{t("contact.telephone")}</dt><dd>{site.telephone}</dd></div>}
        {site.email && <div><dt>{t("contact.email")}</dt><dd><a href={`mailto:${site.email}`}>{site.email}</a></dd></div>}
        {site.fax && <div><dt>{t("contact.fax")}</dt><dd>{site.fax}</dd></div>}
    </dl>;
}

function JlptTestSitesPage() {
    const { t, i18n } = useTranslation("jlptTestSites");
    const isPt = i18n.language === "pt";
    const currentPath = isPt ? "/pt/jlpt-test-sites" : "/jlpt-test-sites";
    const { country: savedCountryCode } = useCountry();
    const [query, setQuery] = useState("");
    const hasPrefilledRef = useRef(false);
    const userHasEditedRef = useRef(false);

    // Prefill the search once from the user's saved profile country, and
    // never again - once the user has typed anything themselves, a later
    // (e.g. slower cloud sync) resolution of their saved country must not
    // clobber what they've already typed.
    useEffect(() => {
        if (hasPrefilledRef.current || userHasEditedRef.current) return;
        if (!savedCountryCode) return;
        const name = getCountryName(savedCountryCode);
        if (!name) return;
        hasPrefilledRef.current = true;
        setQuery(name);
    }, [savedCountryCode]);

    function handleQueryChange(nextQuery: string) {
        userHasEditedRef.current = true;
        setQuery(nextQuery);
    }

    const countries = useMemo(() => [...new Set(jlptOverseasSites.map((site) => site.country))].sort(), []);
    const filtered = useMemo(() => {
        const needle = query.trim().toLocaleLowerCase();
        if (!needle) return jlptOverseasSites;
        return jlptOverseasSites.filter((site) => [site.country, site.city, site.hostInstitution, site.region].filter(Boolean).join(" ").toLocaleLowerCase().includes(needle));
    }, [query]);
    const grouped = useMemo(() => countries.map((country) => ({
        country,
        sites: filtered.filter((site) => site.country === country),
    })).filter((group) => group.sites.length), [countries, filtered]);

    // If the current search text names a known country with no real exam
    // locations, and a data-driven fallback exists for it, offer to switch
    // the search (only the search, never the saved profile value) to that
    // nearby alternative.
    const matchedCountryCode = useMemo(() => findCountryCodeForQuery(query), [query]);
    const fallbackCode = matchedCountryCode ? JLPT_COUNTRY_FALLBACKS[matchedCountryCode] : null;
    const showFallbackSuggestion = Boolean(
        matchedCountryCode
        && !countryCodeHasJlptLocations(matchedCountryCode)
        && fallbackCode
        && countryCodeHasJlptLocations(fallbackCode)
    );
    const fallbackCountryName = fallbackCode ? getCountryName(fallbackCode) : null;
    const matchedCountryName = matchedCountryCode ? getCountryName(matchedCountryCode) : null;

    return <div className={styles.page}>
        <Seo title={t("seoTitle")} description={t("seoDescription")} path={currentPath} lang={isPt ? "pt-PT" : undefined} keywords={t("seoKeywords", { returnObjects: true }) as string[]} />
        <Header />
        <main id="main-content" className={styles.main}>
            <section className={styles.hero} aria-labelledby="jlpt-sites-title">
                <p className={styles.eyebrow}>{t("eyebrow")}</p>
                <h1 id="jlpt-sites-title">{t("title")}</h1>
                <p>{t("intro")}</p>
                <JlptNotice variant="full" className={styles.independence} />
                <div className={styles.dates} aria-label="2026 JLPT test dates">
                    <span><strong>{jlpt2026Sessions.july.label}</strong>{jlpt2026Sessions.july.date}</span>
                    <span><strong>{jlpt2026Sessions.december.label}</strong>{jlpt2026Sessions.december.date}</span>
                </div>
                <p className={styles.source}>{t("lastVerified")} <a href={JLPT_OVERSEAS_DIRECTORY_URL} target="_blank" rel="noopener noreferrer">{t("desktopDirectory")}</a> · <a href={JLPT_OVERSEAS_MOBILE_DIRECTORY_URL} target="_blank" rel="noopener noreferrer">{t("mobileDirectory")}</a></p>
                <p className={styles.source}>
                    {t("relatedIntro")}{" "}
                    <Link to={isPt ? "/pt/blog/jlpt-europe-countries-with-no-test-site" : "/blog/jlpt-europe-countries-with-no-test-site"}>{t("relatedEuropeLink")}</Link>
                    {" "}
                    <Link to={isPt ? "/blog/plano-estudo-jlpt-portugal-sem-exame-no-pais" : "/blog/jlpt-study-plan-no-portugal-test-site"}>{t("relatedStudyPlanLink")}</Link>
                    {" "}
                    <Link to={isPt ? "/blog/como-inscrever-se-no-jlpt-em-portugal" : "/blog/jlpt-registration-portugal"}>{t("relatedRegistrationLink")}</Link>
                </p>
                <p className={styles.source}><Link to={isPt ? "/pt/jlpt/n5-exam-preparation" : "/jlpt/n5-exam-preparation"}>{t("n5PreparationLink")}</Link></p>
            </section>

            <label className={styles.searchLabel} htmlFor="jlpt-site-search">{t("searchLabel")}</label>
            <input id="jlpt-site-search" className={styles.search} value={query} onChange={(event) => handleQueryChange(event.target.value)} placeholder={t("searchPlaceholder")} />
            <p className={styles.resultCount}>{t("resultCount", { count: filtered.length })}</p>

            {showFallbackSuggestion && (
                <p className={styles.fallbackSuggestion} role="status">
                    {t("fallbackSuggestion", { country: matchedCountryName, fallback: fallbackCountryName })}{" "}
                    <button type="button" className={styles.fallbackButton} onClick={() => handleQueryChange(fallbackCountryName ?? "")}>
                        {t("fallbackButton", { fallback: fallbackCountryName })}
                    </button>
                </p>
            )}

            <div className={styles.countryList}>
                {grouped.map(({ country, sites }) => {
                    const notice = sites.find((site) => site.countryNotice)?.countryNotice;
                    const byCity = sites.reduce<Record<string, JlptOverseasSite[]>>((result, site) => {
                        const key = site.city ?? t("locationNotAnnounced");
                        (result[key] ??= []).push(site);
                        return result;
                    }, {});
                    return <section className={styles.country} key={country} aria-labelledby={`country-${country}`}>
                        <h2 id={`country-${country}`}>{country}</h2>
                        {notice && <p className={styles.notice}>{notice}</p>}
                        {Object.entries(byCity).sort(([a], [b]) => a.localeCompare(b)).map(([city, sitesInCity]) => <div className={styles.city} key={city}>
                            <h3>{city}</h3>
                            {sitesInCity.map((site) => <article className={styles.site} key={site.id}>
                                <div className={styles.siteHeading}>
                                    <h4>{site.hostInstitution ?? t("hostInstitutionNotAnnounced")}</h4>
                                    {site.suspensionStatus && <p className={styles.status}>{site.suspensionStatus.replaceAll("-", " ")}</p>}
                                </div>
                                <div className={styles.sessions}>
                                    <SessionBadge available={site.july.available} label={t("session.july")} />
                                    <SessionBadge available={site.december.available} label={t("session.december")} />
                                </div>
                                {site.website && <a className={styles.website} href={site.website} target="_blank" rel="noopener noreferrer">{t("institutionWebsite")}</a>}
                                <Contact site={site} />
                            </article>)}
                        </div>)}
                    </section>;
                })}
            </div>
        </main>
        <Footer />
    </div>;
}

export default JlptTestSitesPage;
